import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { CalendarIcon, ArrowUpRight, Sparkles } from "lucide-react";
import { SERVICES, STUDIO } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["10:00", "12:00", "14:00", "16:00", "18:00", "20:00"];

export default function Booking() {
  const [date, setDate] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", time: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !date || !form.time) {
      toast.error("Preenche todos os campos obrigatórios.");
      return;
    }
    try {
      setLoading(true);
      const payload = { ...form, date: format(date, "yyyy-MM-dd") };
      await axios.post(`${API}/bookings`, payload);
      toast.success("Pedido enviado! Respondemos em 24h ✦");
      setForm({ name: "", email: "", phone: "", service: "", time: "", message: "" });
      setDate(null);
    } catch {
      toast.error("Não foi possível enviar. Tenta novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="bg-cream text-ink py-24 md:py-32 px-5 md:px-10 lg:px-16">
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-5">
          <div className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-4 py-2 mb-6">
            <Sparkles size={14} className="text-lime" />
            <span className="font-body text-xs tracking-widest uppercase">Agendamento</span>
          </div>
          <h2 className="font-display font-extrabold text-5xl md:text-7xl leading-[0.92] mb-6">
            vamos <br/><span className="bg-hot text-cream italic px-4 rounded-2xl inline-block">começar?</span>
          </h2>
          <p className="font-body text-base text-ink/70 leading-relaxed max-w-md mb-8">
            Escolhe serviço, data e hora. Em 24h respondemos com disponibilidade confirmada e orçamento.
          </p>
          <div className="space-y-3 font-body text-sm bg-ink text-cream rounded-3xl p-6">
            <p><span className="text-lime font-bold">Morada</span> · {STUDIO.address}</p>
            <p><span className="text-lime font-bold">Telefone</span> · <a href={`tel:${STUDIO.phone.replace(/\s/g, "")}`} className="hover:text-lime">{STUDIO.phone}</a></p>
            <p><span className="text-lime font-bold">Email</span> · <a href={`mailto:${STUDIO.email}`} className="hover:text-lime">{STUDIO.email}</a></p>
            <p><span className="text-lime font-bold">Instagram</span> · <a href={STUDIO.instagramUrl} target="_blank" rel="noreferrer" className="hover:text-lime">{STUDIO.instagram}</a></p>
            <p><span className="text-lime font-bold">TikTok</span> · <a href={STUDIO.tiktokUrl} target="_blank" rel="noreferrer" className="hover:text-lime">{STUDIO.tiktok}</a></p>
          </div>
        </div>

        <form
          data-testid="booking-form"
          onSubmit={onSubmit}
          className="col-span-12 md:col-span-7 bg-white rounded-3xl border-2 border-ink p-6 md:p-10 space-y-5 shadow-[8px_8px_0_0_#0A0A0A]"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Nome *">
              <Input data-testid="booking-name-input" className="rounded-xl border-ink/20 font-body h-12" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="O teu nome" required />
            </Field>
            <Field label="Email *">
              <Input data-testid="booking-email-input" type="email" className="rounded-xl border-ink/20 font-body h-12" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="email@dominio.pt" required />
            </Field>
            <Field label="Telefone">
              <Input data-testid="booking-phone-input" className="rounded-xl border-ink/20 font-body h-12" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+351" />
            </Field>
            <Field label="Serviço *">
              <Select value={form.service} onValueChange={(v) => update("service", v)}>
                <SelectTrigger data-testid="booking-service-select" className="rounded-xl border-ink/20 font-body h-12">
                  <SelectValue placeholder="Escolhe" />
                </SelectTrigger>
                <SelectContent className="rounded-xl max-h-80">
                  <div className="px-3 py-2 font-body text-[10px] uppercase tracking-widest text-ink/40">Planos</div>
                  {["Start", "Growth", "360"].map((p) => (
                    <SelectItem key={`plan-${p}`} value={`Plano ${p}`} data-testid={`booking-service-plan-${p.toLowerCase()}`} className="font-display font-bold bg-lime/15 text-ink hover:bg-lime focus:bg-lime data-[state=checked]:bg-lime my-1 mx-1 rounded-lg">
                      ✦ Plano {p}
                    </SelectItem>
                  ))}
                  <div className="px-3 py-2 mt-2 border-t border-ink/10 font-body text-[10px] uppercase tracking-widest text-ink/40">Serviços</div>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.title} className="font-body">{s.title}</SelectItem>
                  ))}
                  <div className="border-t border-ink/10 mt-1" />
                  <SelectItem value="Outros" data-testid="booking-service-outros" className="font-body">Outros</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Data *">
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="booking-date-picker" className="w-full flex items-center justify-between border border-ink/20 rounded-xl px-3 h-12 font-body text-sm bg-white hover:bg-cream transition-colors">
                    {date ? format(date, "dd 'de' MMMM", { locale: pt }) : "Selecionar data"}
                    <CalendarIcon size={16} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="rounded-2xl p-0 w-auto" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} locale={pt} initialFocus />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Hora *">
              <Select value={form.time} onValueChange={(v) => update("time", v)}>
                <SelectTrigger data-testid="booking-time-select" className="rounded-xl border-ink/20 font-body h-12">
                  <SelectValue placeholder="Escolhe" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {TIMES.map((t) => (
                    <SelectItem key={t} value={t} className="font-body">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Conta-nos do projeto">
            <Textarea data-testid="booking-message-input" className="rounded-xl border-ink/20 font-body min-h-[120px]" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Géneros, referências, objetivos..." />
          </Field>

          <button
            type="submit"
            data-testid="booking-submit-button"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-3 bg-lime text-ink font-display font-bold text-base px-6 py-4 rounded-full hover:bg-hot hover:text-cream transition-colors disabled:opacity-50"
          >
            {loading ? "A enviar..." : "Enviar pedido ✦"}
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </form>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-body text-xs font-bold uppercase tracking-wider text-ink/50 block mb-2">{label}</span>
      {children}
    </label>
  );
}
